import type { HiType } from '../constants';
import type { CodeableConcept, Coding } from './types';

/**
 * Codes and profile addresses from the ABDM FHIR implementation guide by
 * NRCeS (version 6.5.0, https://nrces.in/ndhm/fhir/r4/).
 */
export const NRCES_PROFILE_BASE = 'https://nrces.in/ndhm/fhir/r4/StructureDefinition';
const profile = (name: string) => `${NRCES_PROFILE_BASE}/${name}`;

export const PROFILES = {
  DocumentBundle: profile('DocumentBundle'),
  PrescriptionRecord: profile('PrescriptionRecord'),
  OPConsultRecord: profile('OPConsultRecord'),
  WellnessRecord: profile('WellnessRecord'),
  Patient: profile('Patient'),
  Practitioner: profile('Practitioner'),
  Organization: profile('Organization'),
  MedicationRequest: profile('MedicationRequest'),
  Condition: profile('Condition'),
  Observation: profile('Observation'),
  ObservationVitalSigns: profile('ObservationVitalSigns'),
  ObservationBodyMeasurement: profile('ObservationBodyMeasurement'),
} as const;

export const SYSTEMS = {
  snomed: 'http://snomed.info/sct',
  loinc: 'http://loinc.org',
  ucum: 'http://unitsofmeasure.org',
  v2IdentifierType: 'http://terminology.hl7.org/CodeSystem/v2-0203',
  ndhmIdentifierType: 'https://nrces.in/ndhm/fhir/r4/CodeSystem/ndhm-identifier-type-code',
  observationCategory: 'http://terminology.hl7.org/CodeSystem/observation-category',
  conditionCategory: 'http://terminology.hl7.org/CodeSystem/condition-category',
  conditionClinical: 'http://terminology.hl7.org/CodeSystem/condition-clinical',
  substitution: 'http://terminology.hl7.org/CodeSystem/v3-substanceAdminSubstitution',
  /** A UUID written as urn:uuid:..., used for document identifiers. */
  uri: 'urn:ietf:rfc:3986',
  /** ABHA numbers, as in the guide's Patient example. */
  abha: 'https://healthid.ndhm.gov.in',
  /** Health Professional Registry IDs, as in the guide's Practitioner example. */
  hpr: 'https://doctor.ndhm.gov.in',
  /** Health Facility Registry IDs, as in the guide's Organization example. */
  hfr: 'https://facility.ndhm.gov.in',
} as const;

/** The assigner named on identifiers Curentia issues: patients' accounts and practices. */
export const CURENTIA_ASSIGNER = 'Curentia';

/** Identifier types the documents use. */
export const IDENTIFIER_TYPES = {
  abha: { system: SYSTEMS.ndhmIdentifierType, code: 'ABHA', display: 'Ayushman Bharat Health Account (ABHA) ID' },
  medicalRecord: { system: SYSTEMS.v2IdentifierType, code: 'MR', display: 'Medical record number' },
  patientInternal: { system: SYSTEMS.v2IdentifierType, code: 'PI', display: 'Patient internal identifier' },
  medicalLicense: { system: SYSTEMS.v2IdentifierType, code: 'MD', display: 'Medical License number' },
  providerNumber: { system: SYSTEMS.v2IdentifierType, code: 'PRN', display: 'Provider number' },
  organization: { system: SYSTEMS.v2IdentifierType, code: 'XX', display: 'Organization identifier' },
} as const satisfies Record<string, Coding>;

interface DocumentType {
  profile: string;
  type: CodeableConcept;
  title: string;
}

const snomedType = (code: string, display: string): CodeableConcept => ({
  coding: [{ system: SYSTEMS.snomed, code, display }],
  text: display,
});

/** Composition.type and profile for each ABDM health information type. */
export const DOCUMENT_TYPES: Readonly<Record<HiType, DocumentType>> = {
  Prescription: { profile: PROFILES.PrescriptionRecord, type: snomedType('440545006', 'Prescription record'), title: 'Prescription record' },
  OPConsultation: { profile: PROFILES.OPConsultRecord, type: snomedType('371530004', 'Clinical consultation report'), title: 'Consultation report' },
  DiagnosticReport: { profile: profile('DiagnosticReportRecord'), type: snomedType('721981007', 'Diagnostic studies report'), title: 'Diagnostic report' },
  DischargeSummary: { profile: profile('DischargeSummaryRecord'), type: snomedType('373942005', 'Discharge summary'), title: 'Discharge summary' },
  ImmunizationRecord: { profile: profile('ImmunizationRecord'), type: snomedType('41000179103', 'Immunization record'), title: 'Immunization record' },
  HealthDocumentRecord: { profile: profile('HealthDocumentRecord'), type: snomedType('419891008', 'Record artifact'), title: 'Health document' },
  WellnessRecord: { profile: PROFILES.WellnessRecord, type: { text: 'Wellness Record' }, title: 'Wellness Record' },
  Invoice: { profile: profile('InvoiceRecord'), type: { text: 'Invoice' }, title: 'Invoice' },
};

const loinc = (code: string, display: string): Coding => ({ system: SYSTEMS.loinc, code, display });

/** LOINC codes for the readings patients record, with UCUM units. */
export const READING_CODES = {
  bloodPressure: loinc('85354-9', 'Blood pressure panel with all children optional'),
  systolic: loinc('8480-6', 'Systolic blood pressure'),
  diastolic: loinc('8462-4', 'Diastolic blood pressure'),
  pulse: loinc('8867-4', 'Heart rate'),
  spo2: loinc('2708-6', 'Oxygen saturation in Arterial blood'),
  spo2PulseOximetry: loinc('59408-5', 'Oxygen saturation in Arterial blood by Pulse oximetry'),
  temperature: loinc('8310-5', 'Body temperature'),
  weight: loinc('29463-7', 'Body weight'),
  bloodGlucose: loinc('2339-0', 'Glucose [Mass/volume] in Blood'),
} as const;
